import { AimOutlined } from "@ant-design/icons";
import {
  MapComponent,
  MapContext,
  SelectFeaturesButton,
} from "@terrestris/react-geo";
import { Col, Descriptions, Divider, Row, Space } from "antd";
import Title from "antd/lib/typography/Title";
import CommonOverlayComponent from "commons/CommonOverlayComponent";
import CommonTable from "commons/CommonTable";
import SelectField from "commons/Forms/antFields/SelectField";
import CommonCard from "commons/Layout/CommonCard";
import PdfExport from "commons/Pdf/PdfExport";
import PdfStatisticsAirTreeView from "commons/Pdf/templates/pdfStatisticsAirtreeView";
import { AuthContext } from "context/auth";
import { find } from "lodash";
import { getCenter } from "ol/extent";
import GeoJSON from "ol/format/GeoJSON";
import OlLayerTile from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import OlMap from "ol/Map";
import OlSourceOsm from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import { Circle, Fill, Stroke, Style } from "ol/style";
import OlView from "ol/View";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ServiceAirTree from "services/airTree";
import ServiceGeoServer from "services/geoServer";
import { getGeomTypeLabel } from "utils/helpers/geo_helpers";
import { FormatDate } from "utils/helpers/date_helpers";
import EChart from "./components/chart";
import { AirTreeSimulationTableColumns } from "./components/tableSimulationColumns";
import { LineString, Point, Polygon } from "ol/geom";
import ViewSimulationProjectElement from "./components/viewSimulationProjectElement";

const arrayChartTypes = [
  {
    substance: "npp",
    main_label: "Sostanza NPP - Assorbimento totale di CO2",
    label_chart: "Media oraria NPP",
    unit: "Kg",
    data_tot: "NPP_tot",
    data_tree: "NPP_tot_tree",
    data_hourly: "NPP_hourly",
  },
  {
    substance: "o3",
    main_label: "Sostanza O3 - Deposizione totale di O3",
    label_chart: "Media oraria O3",
    unit: "g",
    data_tot: "o3tot",
    data_tree: "o3tot_tree",
    data_hourly: "o3tot_hourly",
  },
  {
    substance: "pm10",
    main_label: "Sostanza PM10 - Deposizione totale di PM10",
    label_chart: "Media oraria PM10",
    unit: "g",
    data_tot: "PM10_flux_tot",
    data_tree: "PM10_flux_tree",
    data_hourly: "PM10_flux_hourly",
  },
  {
    substance: "pm25",
    main_label: "Sostanza PM2.5 - Deposizione totale di PM2.5",
    label_chart: "Media oraria PM2.5",
    unit: "g",
    data_tot: "PM2_5_flux_tot",
    data_tree: "PM2_5_flux_tree",
    data_hourly: "PM2_5_flux_hourly",
  },
  {
    substance: "pm1",
    main_label: "Sostanza PM1 - Deposizione totale di PM1",
    label_chart: "Media oraria PM1",
    unit: "g",
    data_tot: "PM1_flux_tot",
    data_tree: "PM1_flux_tree",
    data_hourly: "PM1_flux_hourly",
  },
  {
    substance: "no2",
    main_label: "Sostanza NO2 - Assorbimento totale di NO2",
    label_chart: "Media oraria NO2",
    unit: "g",
    data_tot: "NO2_tot",
    data_tree: "NO2_tot_tree",
    data_hourly: "NO2_hourly",
  },
  {
    substance: "so2",
    main_label: "Sostanza SO2 - Assorbimento totale di SO2",
    label_chart: "Media oraria SO2",
    unit: "g",
    data_tot: "SO2_tot",
    data_tree: "SO2_tot_tree",
    data_hourly: "SO2_hourly",
  },
  {
    substance: "co",
    main_label: "Sostanza CO - Assorbimento totale di CO",
    label_chart: "Media oraria CO",
    unit: "g",
    data_tot: "CO_tot",
    data_tree: "CO_tot_tree",
    data_hourly: "CO_hourly",
  },
];

const AirTreeViewProjectSimulation: FC = () => {
  const { id } = useParams();

  const { currentUser } = useContext(AuthContext);
  const [drawerSimulationVisible, setDrawerSimulationVisible] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [projectHasElements, setProjectHasElements] = useState(false);
  const [currentDataChart, setCurrentDataChart] = useState("NPP_hourly");
  const [mapImageUrl, setMapImageUrl] = useState(null);
  const [treeTypesLegend, setTreeTypesLegend] = useState(null);

  // Map and Vector layers
  const [map, setMap] = useState(null);
  const [mainBbox, setMainBbox] = useState(null);
  const [projectVector, setProjectVector] = useState(null);
  const [projectElementsVector, setProjectElementsVector] = useState(null);
  const [simulationResponse, setSimulationResponse] = useState(null);

  const refChart = useRef(null);
  refChart.current = [];

  const geoServerUrl = process?.env?.REACT_APP_GEOSERVER_URL;

  useEffect(() => {
    if (id) {
      // Get project
      ServiceAirTree.get({
        id: parseInt(id),
        expand: "airtreeProjectElements,totalByTreeType",
      })
        .then((res) => {
          setCurrentProject(res);
        })
        .catch((e) => {
          console.log("Error: ", e);
        });

      ServiceAirTree.treeTypes()
        .then((res) => {
          setTreeTypesLegend(res);
        })
        .catch((e) => {
          console.log("Error: ", e);
        });
    } else {
      setCurrentProject(null);
    }
  }, [id]);

  useEffect(() => {
    if (currentProject) {
      // Get Main Project Vector
      ServiceGeoServer.getProjectWfs({ uuid: currentProject?.uuid }).then(
        (wfsProj) => {
          if (wfsProj.bbox) {
            setMainBbox(wfsProj.bbox);
          }

          const vectorSource = new VectorSource({
            features: new GeoJSON().readFeatures(wfsProj),
            strategy: bboxStrategy,
          });

          const vectorProject = new VectorLayer({
            source: vectorSource,
            style: new Style({
              fill: new Fill({
                color: "rgba(253, 52, 20, 0.5)",
              }),
              stroke: new Stroke({
                color: "rgba(253, 52, 20, 1.0)",
                width: 2,
              }),
            }),
          });
          setProjectVector(vectorProject);
        }
      );

      // Get Project Elements Vector
      ServiceGeoServer.getProjectElementsWfs({
        idProject: currentProject?.id,
      }).then((wfsProjElements) => {
        if (wfsProjElements?.features?.length > 0) {
          setProjectHasElements(true);
        }
        const vectorProjectElementsSource = new VectorSource({
          features: new GeoJSON().readFeatures(wfsProjElements),
          strategy: bboxStrategy,
        });

        // const fill = new Fill({
        //   color: '#ffff00',
        // });

        // const stroke = new Stroke({
        //   color: '#ffff88',
        //   width: 3,
        // });

        const vectorProjectElements = new VectorLayer({
          source: vectorProjectElementsSource,
          style: function (feature, resolution) {
            // If point set number of trees
            if (feature?.getGeometry() instanceof Point) {
              return new Style({
                image: new Circle({
                  fill: new Fill({
                    color: "rgba(46, 204, 113, 0.7)",
                  }),
                  stroke: new Stroke({
                    color: "rgba(46, 204, 113, 1)",
                  }),
                  radius: 6,
                }),
              });
            }

            // If line get measure
            if (feature?.getGeometry() instanceof LineString) {
              return new Style({
                stroke: new Stroke({
                  color: "rgba(255, 229, 10, 1)",
                  width: 3,
                }),
              });
            }

            // If polygon get area
            if (feature?.getGeometry() instanceof Polygon) {
              return new Style({
                fill: new Fill({
                  color: "rgba(52, 152, 219, 0.6)",
                }),
                stroke: new Stroke({
                  color: "rgba(52, 152, 219, 1)",
                  width: 3,
                }),
              });
            }
          },
          // style: new Style({
          //   image: new Circle({
          //     fill: fill,
          //     stroke: stroke,
          //     radius: 10,
          //   }),
          //   fill: fill,
          //   stroke: stroke
          // }),
        });
        setProjectElementsVector(vectorProjectElements);
      });
    }
  }, [currentProject]);

  useEffect(() => {
    if (currentProject) {
      if (mainBbox && projectVector && projectElementsVector) {
        setMap(
          new OlMap({
            layers: [
              new OlLayerTile({
                source: new OlSourceOsm(),
              }),
              projectVector,
              projectElementsVector,
            ],
            view: new OlView({
              center: getCenter(mainBbox),
              zoom: 11,
            }),
          })
        );
      }
    }
  }, [currentProject, mainBbox, projectVector, projectElementsVector]);

  // Set map data/url
  useEffect(() => {
    if (currentProject && map) {
      const dims = {
        a0: [1189, 841],
        a1: [841, 594],
        a2: [594, 420],
        a3: [420, 297],
        a4: [297, 210],
        a5: [210, 148],
      };

      // Get map info e size
      const format = "a4";
      const resolution = 150;
      const dim = dims[format];
      const width = Math.round((dim[0] * resolution) / 25.4);
      const height = Math.round((dim[1] * resolution) / 25.4);
      const size = map.getSize();
      const viewResolution = map.getView().getResolution();

      // Set print size
      const printSize = [width, height];
      map.setSize(printSize);
      const scaling = Math.min(width / size?.[0], height / size?.[1]);
      map.getView().setResolution(viewResolution / scaling);

      // Generate pdf and resize original map
      map.once("rendercomplete", function () {
        const mapCanvas = document.createElement("canvas");
        mapCanvas.width = width;
        mapCanvas.height = height;
        const mapContext = mapCanvas.getContext("2d");
        Array.prototype.forEach.call(
          document.querySelectorAll(".ol-layer canvas"),
          function (canvas) {
            if (canvas.width > 0) {
              const opacity = canvas.parentNode.style.opacity;
              mapContext.globalAlpha = opacity === "" ? 1 : Number(opacity);
              const transform = canvas.style.transform;
              // Get the transform parameters from the style's transform matrix
              const matrix = transform
                .match(/^matrix\(([^\(]*)\)$/)[1]
                .split(",")
                .map(Number);
              // Apply the transform to the export map context
              CanvasRenderingContext2D.prototype.setTransform.apply(
                mapContext,
                matrix
              );
              mapContext.drawImage(canvas, 0, 0);
            }
          }
        );
        mapContext.globalAlpha = 1;

        // Reset original map size
        map.setSize(size);
        map.getView().setResolution(viewResolution);
        setMapImageUrl(mapCanvas.toDataURL("image/jpeg"));
      });
    }
  }, [currentProject, map]);

  // add refs
  const addChartRef = (el, uuid, type) => {
    if (el && !refChart.current.includes(el)) {
      refChart.current.push({ uuid, type, el });
    }
  };

  if (!map) {
    return null;
  }

  const openSimulationData = (data) => {
    if (data) {
      if (typeof data.details == "string") {
        data.details = JSON.parse(data?.details);
      }
      if (typeof data.simulation_response == "string") {
        data.simulation_response = JSON.parse(data?.simulation_response);
      }
      setSimulationResponse(data);
    }
    setDrawerSimulationVisible(true);
    setTimeout(() => {
      setShowChart(true);
    }, 1000);
  };

  return (
    <CommonCard title={<h2>AirTree - Dati simulazione</h2>}>
      <MapContext.Provider value={map}>
        <Row gutter={16}>
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={24}
            xl={24}
            style={{ textAlign: "right", paddingBottom: "20px" }}
          >
            <Space>
              {currentProject?.status >= 1 && (
                <SelectFeaturesButton
                  icon={<AimOutlined />}
                  layers={[projectElementsVector]}
                  onFeatureSelect={(e) =>
                    //@ts-ignore
                    openSimulationData(e.selected[0]?.values_)
                  }
                >
                  Seleziona elemento
                </SelectFeaturesButton>
              )}

              {currentProject?.status === 2 && (
                <PdfExport
                  templateComponent={
                    <PdfStatisticsAirTreeView
                      srcMapImage={mapImageUrl}
                      project={currentProject}
                      refChart={refChart}
                      arrayChartTypes={arrayChartTypes}
                      treeTypesLegend={treeTypesLegend}
                    />
                  }
                />
              )}
            </Space>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <MapComponent
              map={map}
              style={{
                height: "50vh",
              }}
            />
          </Col>
          <Col
            xs={24}
            sm={24}
            md={24}
            lg={24}
            xl={24}
            style={{ paddingTop: "20px" }}
          >
            <Descriptions
              bordered
              title={
                <Title level={3} type="success">
                  Dati progetto
                </Title>
              }
              size={"default"}
              style={{ paddingBottom: "20px" }}
              column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
              //extra={<Button type="primary">Edit</Button>}
            >
              <Descriptions.Item label="Codice Progetto">
                {currentProject?.uuid}
              </Descriptions.Item>
              <Descriptions.Item label="Nome Progetto">
                {currentProject?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Latitudine">
                {currentProject?.centroid_lat?.toFixed(6)}
              </Descriptions.Item>
              <Descriptions.Item label="Longitudine">
                {currentProject?.centroid_lng?.toFixed(6)}
              </Descriptions.Item>
              <Descriptions.Item label="Superficie Km2">
                {currentProject?.area}
              </Descriptions.Item>
              <Descriptions.Item label="Data inizio simulazione">
                {FormatDate(currentProject?.simulation_date_start)}
              </Descriptions.Item>
              <Descriptions.Item label="Data fine simulazione">
                {FormatDate(currentProject?.simulation_date_end)}
              </Descriptions.Item>
              {/* <Descriptions.Item label="Data creazione">{FormatUnixDate(currentProject?.created_at)}</Descriptions.Item>
              <Descriptions.Item label="Data ultima modifica">{FormatUnixDate(currentProject?.created_at)}</Descriptions.Item> */}
            </Descriptions>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={24}
            lg={24}
            xl={24}
            style={{ paddingTop: "20px" }}
          >
            <Descriptions
              bordered
              title={
                <Title level={3} type="success">
                  Superficie totale copertura per specie
                </Title>
              }
              size={"default"}
              style={{ paddingBottom: "20px" }}
              column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
              //extra={<Button type="primary">Edit</Button>}
            >
              {treeTypesLegend &&
                Object.keys(currentProject?.totalByTreeType).map((key) => {
                  if (key != "totalPollutings") {
                    return (
                      <Descriptions.Item
                        key={key}
                        label={
                          find(treeTypesLegend, { id: parseInt(key) })?.label
                        }
                      >
                        {(
                          currentProject?.totalByTreeType?.[key]?.area / 1000000
                        )?.toFixed(4)}{" "}
                        Km2
                      </Descriptions.Item>
                    );
                  }
                })}
            </Descriptions>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={24}
            lg={24}
            xl={24}
            style={{ paddingTop: "20px" }}
          >
            <Descriptions
              bordered
              title={
                <Title level={3} type="success">
                  Assorbimento totale inquinanti
                </Title>
              }
              size={"default"}
              style={{ paddingBottom: "20px" }}
              column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
              //extra={<Button type="primary">Edit</Button>}
            >
              {currentProject?.totalByTreeType?.totalPollutings &&
                Object.keys(
                  currentProject?.totalByTreeType?.totalPollutings
                ).map((key) => {
                  let objTotPoll =
                    currentProject?.totalByTreeType?.totalPollutings?.[key];
                  return (
                    <Descriptions.Item
                      key={key}
                      label={
                        find(arrayChartTypes, { substance: key })?.main_label
                      }
                    >
                      {objTotPoll?.toFixed(2)}{" "}
                      {find(arrayChartTypes, { substance: key })?.unit}
                    </Descriptions.Item>
                  );
                })}
            </Descriptions>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={24}
            lg={24}
            xl={24}
            style={{ paddingTop: "20px" }}
          >
            {currentProject?.airtreeProjectElements?.map((prj) => {
              // If geotype is POLYGON (multi types) loop simulation response
              if (prj?.geom_type === 3) {
                return prj?.simulation_response?.map(
                  (elementSimulationResponse) => {
                    return (
                      <div key={prj?.uuid}>
                        <Divider />
                        <Title level={3} type="success">
                          Simulazione elemento{" "}
                          {getGeomTypeLabel(prj?.geom_type)} (Specie{" "}
                          {
                            find(treeTypesLegend, {
                              id: elementSimulationResponse?.type,
                            })?.label
                          }
                          ){" "}
                        </Title>

                        {arrayChartTypes?.map((chartType) => {
                          if (
                            elementSimulationResponse?.[chartType?.data_hourly]
                          ) {
                            return (
                              <div key={`${prj?.uuid}-${chartType?.substance}`}>
                                <Descriptions
                                  bordered
                                  title={
                                    <Title level={4} type="secondary">
                                      {chartType?.main_label}
                                    </Title>
                                  }
                                  size={"default"}
                                  style={{
                                    paddingBottom: "20px",
                                    marginTop: "20px",
                                  }}
                                  column={{
                                    xxl: 1,
                                    xl: 1,
                                    lg: 1,
                                    md: 1,
                                    sm: 1,
                                    xs: 1,
                                  }}
                                  //extra={<Button type="primary">Edit</Button>}
                                >
                                  <Descriptions.Item label="Totale per m2">
                                    {elementSimulationResponse?.[
                                      chartType?.data_tot
                                    ]?.toFixed(4)}{" "}
                                    {chartType?.unit}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="Totale per albero">
                                    {elementSimulationResponse?.[
                                      chartType?.data_tree
                                    ]?.toFixed(4)}{" "}
                                    {chartType?.unit}
                                  </Descriptions.Item>
                                </Descriptions>

                                <EChart
                                  ref={(el) =>
                                    addChartRef(
                                      el,
                                      prj?.uuid,
                                      chartType?.substance
                                    )
                                  }
                                  datas={
                                    elementSimulationResponse?.[
                                      chartType?.data_hourly
                                    ]
                                  }
                                />
                              </div>
                            );
                          }
                        })}
                      </div>
                    );
                  }
                );
              } else {
                return (
                  <div key={prj?.uuid}>
                    <Divider />
                    <Title level={3} type="success">
                      Simulazione elemento {getGeomTypeLabel(prj?.geom_type)}{" "}
                      (Specie{" "}
                      {find(treeTypesLegend, { id: prj?.details?.type })?.label}
                      ){" "}
                    </Title>

                    {arrayChartTypes?.map((chartType) => {
                      // If geotype is POLYGON (multi types) loop simulation response
                      if (prj?.simulation_response?.[chartType?.data_hourly]) {
                        return (
                          <div key={`${prj?.uuid}-${chartType?.substance}`}>
                            <Descriptions
                              bordered
                              title={
                                <Title level={4} type="secondary">
                                  {chartType?.main_label}
                                </Title>
                              }
                              size={"default"}
                              style={{
                                paddingBottom: "20px",
                                marginTop: "20px",
                              }}
                              column={{
                                xxl: 1,
                                xl: 1,
                                lg: 1,
                                md: 1,
                                sm: 1,
                                xs: 1,
                              }}
                              //extra={<Button type="primary">Edit</Button>}
                            >
                              <Descriptions.Item label="Totale per m2">
                                {prj?.simulation_response?.[
                                  chartType?.data_tot
                                ]?.toFixed(4)}{" "}
                                {chartType?.unit}
                              </Descriptions.Item>
                              <Descriptions.Item label="Totale per albero">
                                {prj?.simulation_response?.[
                                  chartType?.data_tree
                                ]?.toFixed(4)}{" "}
                                {chartType?.unit}
                              </Descriptions.Item>
                            </Descriptions>

                            <EChart
                              ref={(el) =>
                                addChartRef(el, prj?.uuid, chartType?.substance)
                              }
                              datas={
                                prj?.simulation_response?.[
                                  chartType?.data_hourly
                                ]
                              }
                            />
                          </div>
                        );
                      }
                    })}
                  </div>
                );
              }
            })}
          </Col>
        </Row>
      </MapContext.Provider>

      <CommonOverlayComponent
        title={"Scheda elemento"}
        type="drawer"
        destroyOnClose={true}
        hasButton={false}
        visible={drawerSimulationVisible}
        width={"70%"}
        onCancel={() => {
          setDrawerSimulationVisible(false);
          setShowChart(false);
        }}
      >
        {simulationResponse?.simulation_response && showChart ? (
          <ViewSimulationProjectElement
            simulationData={simulationResponse}
            project={currentProject}
          />
        ) : (
          <Title level={4} type="danger">
            Dati simulazione non disponibili per l'elemento selezionato
          </Title>
        )}
      </CommonOverlayComponent>
    </CommonCard>
  );
};

export default AirTreeViewProjectSimulation;
